import { defineStore } from 'pinia'
import { useRequest } from 'composables/useRequest.js'

const PAGE_LIMIT = 30

// GET requests go through axios.get(url, { headers }) — query params must be
// embedded in the URL itself, they aren't sent from a request body.
function buildQuery(filters, sort, page) {
  const parts = [`page[number]=${page}`, `page[limit]=${PAGE_LIMIT}`, `sort_by=${sort.by}`, `sort_order=${sort.order}`]
  if (filters.search) parts.push(`search=${encodeURIComponent(filters.search)}`)
  filters.criticality.forEach((value) => parts.push(`criticality[]=${encodeURIComponent(value)}`))
  return parts.join('&')
}

export const useOrgApplicationsStore = defineStore('orgApplications', {
  state: () => ({
    organizationId: null,
    applications: [],
    pageInfo: { current_page: 0, total_pages: 0, total_records: 0 },
    filters: { search: '', criticality: [] },
    sort: { by: 'name', order: 'asc' },
    loading: false,
    loadingMore: false,
    error: null,
    fetchedAt: null,
  }),

  getters: {
    hasMore: (state) => state.pageInfo.current_page === 0 || state.pageInfo.current_page < state.pageInfo.total_pages,
    hasActiveFilters: (state) => !!state.filters.search || state.filters.criticality.length > 0,
  },

  actions: {
    async init(organizationId) {
      if (this.organizationId === organizationId && this.fetchedAt) return
      this.organizationId = organizationId
      this.filters = { search: '', criticality: [] }
      this.sort = { by: 'name', order: 'asc' }
      await this.fetchPage(true)
    },

    async setSort(by, order) {
      this.sort = { by, order }
      await this.fetchPage(true)
    },

    async setSearch(value) {
      this.filters.search = value
      await this.fetchPage(true)
    },

    async toggleCriticality(value) {
      const idx = this.filters.criticality.indexOf(value)
      if (idx === -1) this.filters.criticality.push(value)
      else this.filters.criticality.splice(idx, 1)
      await this.fetchPage(true)
    },

    async clearFilters() {
      this.filters = { search: '', criticality: [] }
      await this.fetchPage(true)
    },

    async fetchMore() {
      if (this.loadingMore || this.loading || !this.hasMore) return
      await this.fetchPage(false)
    },

    async fetchPage(reset) {
      if (reset) {
        this.loading = true
        this.error = null
      } else {
        this.loadingMore = true
      }

      const nextPage = reset ? 1 : this.pageInfo.current_page + 1
      const query = buildQuery(this.filters, this.sort, nextPage)

      try {
        const { axiosRequest } = useRequest()
        const res = await axiosRequest('GET', `/api/v1/organizations/${this.organizationId}/org_applications?${query}`, {}, false)
        if (!res.js_error && res.response.success) {
          const { records, page } = res.response.data
          this.applications = reset ? records : [...this.applications, ...records]
          this.pageInfo = page
          this.fetchedAt = Date.now()
        } else {
          this.error = 'Failed to load applications.'
        }
      } catch {
        this.error = 'Something went wrong.'
      } finally {
        this.loading = false
        this.loadingMore = false
      }
    },

    // Standalone fetch for the factsheet page, which may be opened directly
    // (deep link, refresh) without the list page ever having loaded.
    async fetchOne(organizationId, applicationId) {
      this.organizationId = organizationId
      const { axiosRequest } = useRequest()
      return axiosRequest('GET', `/api/v1/organizations/${organizationId}/org_applications/${applicationId}`, {}, false)
    },

    async addApplication(payload) {
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('POST', `/api/v1/organizations/${this.organizationId}/org_applications`, { org_application: payload }, false)
      if (!res.js_error && res.response.success) {
        await this.fetchPage(true)
      }
      return res
    },

    async updateApplication(applicationId, payload) {
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('PUT', `/api/v1/organizations/${this.organizationId}/org_applications/${applicationId}`, { org_application: payload }, false)
      if (!res.js_error && res.response.success) {
        const application = this.applications.find((a) => a.id === applicationId)
        if (application) Object.assign(application, res.response.data)
      }
      return res
    },

    async duplicateApplication(applicationId) {
      const { axiosRequest } = useRequest()
      const path = `/api/v1/organizations/${this.organizationId}/org_applications/${applicationId}/duplicate`
      const res = await axiosRequest('POST', path, {}, false)
      if (!res.js_error && res.response.success) {
        this.applications.unshift(res.response.data)
        this.pageInfo.total_records += 1
      }
      return res
    },

    async deleteApplication(applicationId) {
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('DELETE', `/api/v1/organizations/${this.organizationId}/org_applications/${applicationId}`, {}, false)
      if (!res.js_error && res.response.success) {
        this.applications = this.applications.filter((a) => a.id !== applicationId)
        this.pageInfo.total_records = Math.max(0, this.pageInfo.total_records - 1)
      }
      return res
    },

    async attachDepartments(applicationId, departmentIds) {
      const { axiosRequest } = useRequest()
      return axiosRequest('POST', `/api/v1/organizations/${this.organizationId}/org_applications/${applicationId}/departments`, { department_ids: departmentIds }, false)
    },

    async detachDepartment(applicationId, departmentId) {
      const { axiosRequest } = useRequest()
      return axiosRequest('DELETE', `/api/v1/organizations/${this.organizationId}/org_applications/${applicationId}/departments/${departmentId}`, {}, false)
    },

    // payload is { org_component_version_id }.
    async attachDependency(applicationId, payload) {
      const { axiosRequest } = useRequest()
      const path = `/api/v1/organizations/${this.organizationId}/org_applications/${applicationId}/factsheet_dependencies`
      return axiosRequest('POST', path, payload, false)
    },

    async updateDependency(applicationId, dependencyId, payload) {
      const { axiosRequest } = useRequest()
      const path = `/api/v1/organizations/${this.organizationId}/org_applications/${applicationId}/factsheet_dependencies/${dependencyId}`
      return axiosRequest('PUT', path, payload, false)
    },

    async detachDependency(applicationId, dependencyId) {
      const { axiosRequest } = useRequest()
      const path = `/api/v1/organizations/${this.organizationId}/org_applications/${applicationId}/factsheet_dependencies/${dependencyId}`
      return axiosRequest('DELETE', path, {}, false)
    },
  },
})
